import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Card, Row, Col, Form, InputGroup, Alert } from 'react-bootstrap';
import { Search, Edit, Trash, PlusCircle, RefreshCw } from 'lucide-react';
import { getAllUsers, deleteUser } from '../../api/users';
import useAuth from '../../hooks/useAuth';

const UserList = ({ onAddUser, onEditUser }) => {
    const { isAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getAllUsers();
                setUsers(Array.isArray(data) ? data : []);
                setFilteredUsers(Array.isArray(data) ? data : []);
            } catch (err) {
                setError('Failed to load users. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [refreshTrigger]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    }, [searchTerm, users]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await deleteUser(id);
                setUsers(users.filter(user => user.id !== id));
                setFilteredUsers(filteredUsers.filter(user => user.id !== id));
            } catch (err) {
                setError('Failed to delete user. Please try again.');
                console.error(err);
            }
        }
    };

    const getRoleBadge = (role) => {
        const roleType = role.name; // Remove 'ROLE_' prefix

        switch (roleType) {
            case 'ADMIN':
                return <Badge bg="danger">Admin</Badge>;
            case 'MANAGER':
                return <Badge bg="primary">Manager</Badge>;
            case 'SALES_REP':
                return <Badge bg="info">Commercial</Badge>;
            case 'DISTRIBUTOR':
                return <Badge bg="success">Distributeur</Badge>;
            case 'WAREHOUSE_MANAGER':
                return <Badge bg="warning">Manager entrepot</Badge>;
            default:
                return <Badge bg="secondary">{roleType}</Badge>;
        }
    };

    // Helpers réutilisés (tableau + cartes)
    const renderRoles = (user) =>
        user.roles && user.roles.length > 0 ? (
            user.roles.map((role, index) => (
                <span key={index} className="me-1">
                    {getRoleBadge(role)}
                </span>
            ))
        ) : (
            <span className="text-muted">Aucun rôle</span>
        );

    const renderStatus = (user) => (
        <>
            {user.active ? (
                <Badge bg="success">Actif</Badge>
            ) : (
                <Badge bg="danger">Inactif</Badge>
            )}
            {user.firstLogin && (
                <Badge bg="warning" className="ms-1">P Login</Badge>
            )}
        </>
    );

    const renderActions = (user) => (
        <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button
                variant="outline-primary"
                size="sm"
                onClick={() => onEditUser(user.id)}
                title="Modifier"
            >
                <Edit size={16} />
            </Button>
            {isAdmin && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(user.id)}
                    title="Supprimer"
                >
                    <Trash size={16} />
                </Button>
            )}
        </div>
    );

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center g-2">
                    <Col xs={12} sm>
                    </Col>
                    <Col xs={12} sm="auto">
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                variant="success"
                                size="sm"
                                onClick={onAddUser}
                                disabled={!isAdmin}
                            >
                                <PlusCircle size={16} className="me-1" />
                                Ajouter Utilisateur
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                            >
                                <RefreshCw size={16} />
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <InputGroup className="mb-3">
                    <InputGroup.Text>
                        <Search size={18} />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Rechercher utilisateur par nom, identifiant ou adresse e-mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Chargement utilisateurs...</p>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        Aucun utilisateur trouvé
                    </div>
                ) : (
                    <>
                        {/* ≥ lg : tableau */}
                        <div className="table-responsive d-none d-lg-block">
                            <Table striped hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Nom</th>
                                        <th>Nom d'utilisateur</th>
                                        <th>Email</th>
                                        <th>Roles</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.fullName}</td>
                                            <td>{user.username}</td>
                                            <td>{user.email}</td>
                                            <td>{renderRoles(user)}</td>
                                            <td>{renderStatus(user)}</td>
                                            <td>{renderActions(user)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* < lg : cartes */}
                        <div className="d-lg-none">
                            {filteredUsers.map(user => (
                                <Card key={user.id} className="mb-2 shadow-sm">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <div className="fw-bold">{user.fullName}</div>
                                                <small className="text-muted">{user.username}</small>
                                            </div>
                                            <div className="text-end">
                                                {renderStatus(user)}
                                            </div>
                                        </div>

                                        <div className="small mb-1 text-break">
                                            <span className="text-muted">Email : </span>
                                            {user.email}
                                        </div>

                                        <div className="small mb-3">
                                            <span className="text-muted">Rôles : </span>
                                            {renderRoles(user)}
                                        </div>

                                        {renderActions(user)}
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default UserList;